import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  LinearProgress,
  Alert,
  Tabs,
  Tab,
  TablePagination,
  TextField,
  InputAdornment,
  useTheme,
  alpha,
} from '@mui/material';
import {
  Search,
  AccountBalance,
  Payment as PaymentIcon,
} from '@mui/icons-material';
import { getAllPayments } from '../../../services/request/Payments';
import { getBorrowers } from '../../../services/request/Borrowers';
import { getAllDebits } from '../../../services/request/Debits';
import { formatCurrency } from '../../../utils/CurrencyFormat';
import { toast } from 'react-toastify';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`payments-tabpanel-${index}`}
      aria-labelledby={`payments-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

interface Payment {
  id: string;
  debitId: string;
  paymentType: string;
  amount: number;
  note?: string;
  paymentReceivedBy?: string;
  status?: string;
  borrowerName?: string;
  createdDate: string;
  updatedDate: string;
}

interface Debit {
  id: string;
  borrowerId: string;
  borrowerName?: string;
  amount: number;
  outstanding: number;
  status: string;
  createdDate: string;
  updatedDate: string;
}

interface Borrower {
  id: string;
  name: string;
  email?: string;
  contactNumber?: string;
}

const PaymentsAndDebits: React.FC = () => {
  const theme = useTheme();
  const [tabValue, setTabValue] = useState(0);
  const [payments, setPayments] = useState<Payment[]>([]);
  const [debits, setDebits] = useState<Debit[]>([]);
  const [borrowers, setBorrowers] = useState<Record<string, Borrower>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Pagination for payments
  const [paymentsPage, setPaymentsPage] = useState(0);
  const [paymentsRowsPerPage, setPaymentsRowsPerPage] = useState(25);
  const [totalPayments, setTotalPayments] = useState(0);
  
  // Pagination for debits
  const [debitsPage, setDebitsPage] = useState(0);
  const [debitsRowsPerPage, setDebitsRowsPerPage] = useState(25);
  const [totalDebits, setTotalDebits] = useState(0);

  useEffect(() => {
    fetchBorrowers();
  }, []);

  useEffect(() => {
    if (tabValue === 0) {
      fetchPayments();
    } else if (tabValue === 1) {
      fetchAllDebits();
    }
  }, [tabValue, paymentsPage, paymentsRowsPerPage, debitsPage, debitsRowsPerPage]);

  const fetchBorrowers = async () => {
    try {
      const response = await getBorrowers(0, 1000, {});
      if (response.code === 200 && response.result) {
        const borrowersMap: Record<string, Borrower> = {};
        response.result.forEach((borrower: Borrower) => {
          borrowersMap[borrower.id] = borrower;
        });
        setBorrowers(borrowersMap);
      }
    } catch (err) {
      console.error('Error fetching borrowers:', err);
    }
  };

  const fetchPayments = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getAllPayments(paymentsPage, paymentsRowsPerPage);
      
      if (response && (response.code === 200 || response.statusCode === 200)) {
        const paymentsData = response.data || response;
        setPayments(paymentsData.result || paymentsData.payments || []);
        setTotalPayments(paymentsData.totalCount || paymentsData.count || 0);
      } else {
        setPayments(response?.result || response?.payments || []);
        setTotalPayments(response?.totalCount || response?.count || 0);
      }
    } catch (err: any) {
      console.error('Error fetching payments:', err);
      setError('Failed to load payments');
      toast.error('Failed to load payments');
      setPayments([]);
      setTotalPayments(0);
    } finally {
      setLoading(false);
    }
  };

  const fetchAllDebits = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await getAllDebits(debitsPage, debitsRowsPerPage, {});
      
      if (response && (response.code === 200 || response.statusCode === 200)) {
        const debitsData = response.data || response;
        setDebits(debitsData.result || debitsData.debits || []);
        setTotalDebits(debitsData.totalCount || debitsData.count || 0);
      } else {
        setDebits(response?.result || response?.debits || []);
        setTotalDebits(response?.totalCount || response?.count || 0);
      }
    } catch (err: any) {
      console.error('Error fetching debits:', err);
      setError('Failed to load debits');
      toast.error('Failed to load debits');
      setDebits([]);
      setTotalDebits(0);
    } finally {
      setLoading(false);
    }
  };


  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handlePaymentsPageChange = (event: unknown, newPage: number) => {
    setPaymentsPage(newPage);
  };

  const handlePaymentsRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPaymentsRowsPerPage(parseInt(event.target.value, 10));
    setPaymentsPage(0);
  };

  const handleDebitsPageChange = (event: unknown, newPage: number) => {
    setDebitsPage(newPage);
  };

  const handleDebitsRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setDebitsRowsPerPage(parseInt(event.target.value, 10));
    setDebitsPage(0);
  };

  const filteredPayments = payments.filter((payment) => {
    if (!searchTerm) return true;
    const borrowerId = debits.find((d) => d.id === payment.debitId)?.borrowerId || '';
    const borrower = borrowers[borrowerId];
    const customerName = payment.borrowerName || borrower?.name || '';
    const searchLower = searchTerm.toLowerCase();
    return (
      customerName.toLowerCase().includes(searchLower) ||
      borrower?.email?.toLowerCase().includes(searchLower) ||
      borrower?.contactNumber?.includes(searchTerm) ||
      payment.amount.toString().includes(searchTerm) ||
      payment.paymentType?.toLowerCase().includes(searchLower)
    );
  });

  const filteredDebits = debits.filter((debit) => {
    if (!searchTerm) return true;
    const borrower = borrowers[debit.borrowerId];
    const customerName = debit.borrowerName || borrower?.name || '';
    const searchLower = searchTerm.toLowerCase();
    return (
      customerName.toLowerCase().includes(searchLower) ||
      borrower?.email?.toLowerCase().includes(searchLower) ||
      borrower?.contactNumber?.includes(searchTerm) ||
      debit.amount.toString().includes(searchTerm) ||
      debit.status?.toLowerCase().includes(searchLower)
    );
  });

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 700 }}>
          Payments & Debits
        </Typography>
        <Typography variant="body1" color="text.secondary">
          View all payments and debits across all customers
        </Typography>
      </Box>

      <Paper elevation={3} sx={{ p: 3 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
          <Tabs value={tabValue} onChange={handleTabChange}>
            <Tab label="Payments" icon={<PaymentIcon />} iconPosition="start" />
            <Tab label="Debits" icon={<AccountBalance />} iconPosition="start" />
          </Tabs>
        </Box>

        <Box sx={{ mb: 3 }}>
          <TextField
            fullWidth
            placeholder="Search by customer name, email, phone, amount..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
            sx={{ maxWidth: 500 }}
          />
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Payments Tab */}
        <TabPanel value={tabValue} index={0}>
          {loading && payments.length === 0 ? (
            <Box sx={{ p: 3 }}>
              <LinearProgress />
              <Typography sx={{ mt: 2 }}>Loading payments...</Typography>
            </Box>
          ) : filteredPayments.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <Typography variant="h6" color="text.secondary">
                No payments found
              </Typography>
            </Box>
          ) : (
            <>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Customer Name</TableCell>
                      <TableCell align="right">Amount</TableCell>
                      <TableCell>Payment Type</TableCell>
                      <TableCell>Date</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Received By</TableCell>
                      <TableCell>Note</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredPayments.map((payment) => {
                      const debit = debits.find((d) => d.id === payment.debitId);
                      const borrower = borrowers[debit?.borrowerId || ''];
                      // Use borrowerName from API response if available, otherwise fallback to borrowers map
                      const customerName = payment.borrowerName || borrower?.name || 'Unknown Customer';
                      return (
                        <TableRow key={payment.id} hover>
                          <TableCell>
                            <Typography variant="body2" fontWeight="medium">
                              {customerName}
                            </Typography>
                            {borrower?.email && (
                              <Typography variant="caption" color="text.secondary" display="block">
                                {borrower.email}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" fontWeight="bold" color="success.main">
                              {formatCurrency(payment.amount)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={payment.paymentType || 'N/A'}
                              size="small"
                              color={
                                payment.paymentType === 'REPAYMENT'
                                  ? 'success'
                                  : payment.paymentType === 'INTEREST'
                                  ? 'warning'
                                  : 'default'
                              }
                            />
                          </TableCell>
                          <TableCell>
                            {new Date(payment.createdDate).toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit',
                            })}
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={payment.status || 'N/A'}
                              size="small"
                              color={payment.status === 'APPROVED' ? 'success' : 'default'}
                            />
                          </TableCell>
                          <TableCell>{payment.paymentReceivedBy || '-'}</TableCell>
                          <TableCell>
                            <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                              {payment.note || '-'}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                count={totalPayments}
                page={paymentsPage}
                onPageChange={handlePaymentsPageChange}
                rowsPerPage={paymentsRowsPerPage}
                onRowsPerPageChange={handlePaymentsRowsPerPageChange}
                rowsPerPageOptions={[10, 25, 50, 100]}
              />
            </>
          )}
        </TabPanel>

        {/* Debits Tab */}
        <TabPanel value={tabValue} index={1}>
          {loading && debits.length === 0 ? (
            <Box sx={{ p: 3 }}>
              <LinearProgress />
              <Typography sx={{ mt: 2 }}>Loading debits...</Typography>
            </Box>
          ) : filteredDebits.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <Typography variant="h6" color="text.secondary">
                No debits found
              </Typography>
            </Box>
          ) : (
            <>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Customer Name</TableCell>
                      <TableCell align="right">Amount</TableCell>
                      <TableCell align="right">Outstanding</TableCell>
                      <TableCell>Date</TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredDebits.map((debit) => {
                      const borrower = borrowers[debit.borrowerId];
                      // Use borrowerName from API response if available, otherwise fallback to borrowers map
                      const customerName = debit.borrowerName || borrower?.name || 'Unknown Customer';
                      return (
                        <TableRow key={debit.id} hover>
                          <TableCell>
                            <Typography variant="body2" fontWeight="medium">
                              {customerName}
                            </Typography>
                            {borrower?.email && (
                              <Typography variant="caption" color="text.secondary" display="block">
                                {borrower.email}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" fontWeight="bold" color="primary">
                              {formatCurrency(debit.amount)}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" fontWeight="bold" color="error">
                              {formatCurrency(debit.outstanding)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {new Date(debit.createdDate).toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit',
                            })}
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={debit.status || 'N/A'}
                              size="small"
                              color={
                                debit.status === 'ACTIVE'
                                  ? 'success'
                                  : debit.status === 'CLOSED'
                                  ? 'default'
                                  : 'warning'
                              }
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                count={totalDebits}
                page={debitsPage}
                onPageChange={handleDebitsPageChange}
                rowsPerPage={debitsRowsPerPage}
                onRowsPerPageChange={handleDebitsRowsPerPageChange}
                rowsPerPageOptions={[10, 25, 50, 100]}
              />
            </>
          )}
        </TabPanel>
      </Paper>
    </Container>
  );
};

export default PaymentsAndDebits;
